import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Collapse, Divider, IconButton, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import type { CSSProperties, FC } from "react";
import { useTranslation } from "react-i18next";
import { Popover as TinyPopover } from "react-tiny-popover";
import { EditTimePopover } from "../../../../components/EditTimePopover";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { DaysInWeek, DaysInWeekSelector } from "../../../../components/RecurringShift/util/DaysInWeekSelector";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { JobSelector } from "../../../../components/UIToolkit/molecules/JobSelector";
import { RoleChips } from "../../../../components/UIToolkit/molecules/RoleChips";
import { SystemBanner } from "../../../../components/UIToolkit/molecules/SystemBanner";
import { SystemDatePickerPopup } from "../../../../components/UIToolkit/Popups/SystemDatePickerPopup";
import { SYSTEM_POPOVER_AVAILABLE_HEIGHT_VAR } from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useJobs } from "../../../../hooks/useJobs";
import { ALPHA, INK, INK_BLACK, MOTION, NEUTRAL, STATUS, SURFACE } from "../../../../theme/tokens";
import { DepartmentType, EditDepartmentLocationConfigMode, NurseFullRoleShort, NurseRole } from "../../../../utils/models";
import { useLocationCellContext } from "../Cells/LocationCellContext";
import { smallLabelTypographySx } from "./consts";
import { LocationEmrMappingSection } from "./LocationEmrMappingSection";
import { RatioLadderSection } from "./RatioLadderSection";
import { UpdateShiftsWarning } from "./UpdateShiftsWarning";
import { type EditDepartmentLocationConfigProps, useEditDepartmentLocationConfigState } from "./useEditDepartmentLocationConfigState";

/** The form's own inset. Shared so the pinned action bar can reach across it — see its `bottom`. */
const SCROLL_CONTAINER_PADDING_PX = 8;

const regularFieldWidth = "160px" as const satisfies CSSProperties["width"];
const smallNumericFieldWidth = "100px" as const satisfies CSSProperties["width"];
const regularFieldSx: SxProps = {
	width: regularFieldWidth,
} as const satisfies SxProps;
const smallNumericFieldSx: SxProps = {
	width: smallNumericFieldWidth,
} as const satisfies SxProps;

const ctaButtonWidth = "100px" as const satisfies CSSProperties["width"];

/**
 * Height reveal for the blocks that appear once an out-of-config edit starts moving real shifts.
 * Collapse nests children in plain divs, so the inner wrapper has to re-establish the column layout
 * they had as direct flex items of the dialog.
 */
const revealSx: SxProps<Theme> = {
	width: "100%",
	transitionTimingFunction: MOTION.EASE_OUT,
	"& .MuiCollapse-wrapperInner": {
		display: "flex",
		flexDirection: "column",
		gap: 2,
		alignItems: "start",
	},
	"@media (prefers-reduced-motion: reduce)": { transitionDuration: "0ms" },
};

export const EditDepartmentLocationConfig: FC<EditDepartmentLocationConfigProps> = (props) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { chosenDepartment } = useHospitalData();
	const enabledJobs = useJobs();
	const { weekStartDay: contextWeekStartDay } = useLocationCellContext();
	const dialogWeekStartDay = contextWeekStartDay ?? 0;
	const isMasterScheduleEditBlocked = chosenDepartment.disable_edit_master_schedule;
	const {
		configSlimData,
		updateFromDate,
		datePickerAnchorEl,
		setDatePickerAnchorEl,
		editTimePopoverParentRef,
		editTimeOpen,
		setEditTimeOpen,
		times,
		handleSelectedRoleFiltersChanged,
		isSavedEnabled,
		mode,
		existingConfig,
		onCancel,
		handleDeleteLocation,
		deleteLoading,
		setConfirmDelete,
		confirmDelete,
		updateDepartmentLocationsLoading,
		handleSetConfigSlimData,
		handleUpdateFromDate,
		allowDelete,
		selectedSectionOption,
		uniqueLocationsOptions,
		addSectionOption,
		ondDropdownOptionSelect,
		handleSomeDropdownOpen,
		handleSomeDropdownClose,
		isSynergyMS,
		saveDisabledReason,
		availableUnits,
		availableFloors,
		availableRooms,
		emrOptionsAvailable,
		roomsNotice,
		offeredMapping,
		departmentWideCensus,
		handleDepartmentWideCensusChange,
		handleEmrMappingChange,
		locationNameOptions,
		handleSelectLocationName,
		canEditRatioLadder,
		locationRoles,
		ratioLadderGapText,
		handleAddRow,
		handleRemoveRow,
		handleUpdateRow,
		disabledDaysPerRow,
		handleSaveWithDayAmounts,
		handleCreateWithDayAmounts,
		rowHasOccupiedDays,
		jobManagementEnabled,
		handleSelectedJobChanged,
		locationJobId,
		shiftMatchChanges,
		willUpdateExistingShifts,
		applyFromDateAffectsShifts,
	} = useEditDepartmentLocationConfigState(props);

	return (
		<Box
			data-testid="edit-location-config"
			sx={{
				display: "flex",
				p: `${SCROLL_CONTAINER_PADDING_PX}px`,
				flexDirection: "column",
				gap: 2,
				justifyContent: "start",
				alignItems: "start",
				width: "fit-content",
				// 80vh normally, but never more than a host popover says it has room for — a
				// viewport-relative cap inside a shorter popover scrolls this form inside a box taller
				// than its container, leaving Save past the clip edge and unreachable. The fallback
				// keeps the plain 80vh wherever the variable isn't set.
				maxHeight: `min(80vh, var(${SYSTEM_POPOVER_AVAILABLE_HEIGHT_VAR}, 80vh))`,
				overflow: "auto",
				zIndex: 1,
			}}
		>
			<Typography
				style={{
					fontWeight: 600,
					fontStyle: "normal",
					fontSize: "26px",
					lineHeight: "100%",
					letterSpacing: "0%",
					color: theme.palette.primary.main,
					maxWidth: "500px",
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				{mode} - {configSlimData.name ? configSlimData.name : !existingConfig ? "New Location" : "No name"}
			</Typography>
			{/* Section / Location name */}
			<Box
				sx={{
					display: "flex",
					gap: 1,
					width: "100%",
				}}
			>
				<Box key={"section-dropdown-box"} id={"section-dropdown-box"}>
					<SystemDropdown
						onOpen={handleSomeDropdownOpen}
						onClose={handleSomeDropdownClose}
						createNewOption={{
							enabled: true,
							label: "Add Section",
							onCreate: addSectionOption,
						}}
						label={isSynergyMS ? "Region" : "Section"}
						selectSx={regularFieldSx}
						items={uniqueLocationsOptions}
						selectedItem={selectedSectionOption}
						onChange={ondDropdownOptionSelect}
					/>
				</Box>
				{!props.hideNameField && (
					<SystemDropdown
						onOpen={handleSomeDropdownOpen}
						onClose={handleSomeDropdownClose}
						createNewOption={{
							enabled: true,
							label: isSynergyMS ? t("schedule.template.facility.add") : t("schedule.template.location.name.add"),
							onCreate: handleSelectLocationName,
						}}
						label={isSynergyMS ? t("schedule.template.v3.editor.facility") : t("schedule.template.v3.editor.locationName")}
						selectSx={regularFieldSx}
						items={locationNameOptions}
						// Options are trimmed names, as Save writes them, so a stored name with stray spaces still shows.
						selectedItem={configSlimData.name.trim()}
						onChange={handleSelectLocationName}
						data-testid="location-name-dropdown"
					/>
				)}
			</Box>
			<Divider flexItem />
			{/* EMR census area: unit / floor / rooms, or the whole department */}
			{emrOptionsAvailable && (
				<LocationEmrMappingSection
					availableUnits={availableUnits}
					availableFloors={availableFloors}
					availableRooms={availableRooms}
					mapping={offeredMapping}
					departmentWideCensus={departmentWideCensus}
					onDepartmentWideCensusChange={handleDepartmentWideCensusChange}
					lockAreasWhenDepartmentWide={canEditRatioLadder}
					onMappingChange={handleEmrMappingChange}
					onDropdownOpen={handleSomeDropdownOpen}
					onDropdownClose={handleSomeDropdownClose}
				/>
			)}
			{roomsNotice === "rooms_loading" && (
				<Typography role="status" data-testid="location-emr-rooms-loading" sx={{ ...smallLabelTypographySx, maxWidth: "500px" }}>
					{t("schedule.template.census.scope.rooms.loading")}
				</Typography>
			)}
			{roomsNotice === "rooms_failed" && (
				<SystemBanner
					tone="error"
					title={t("locations.emrEdit.roomsError.title")}
					description={t("schedule.template.census.scope.rooms.failed")}
					dataTestId="location-emr-rooms-error"
				/>
			)}
			{/* Facility type (Synergy MS only) */}
			{isSynergyMS && (
				<>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
							width: "100%",
							alignItems: "start",
						}}
					>
						<SystemDropdown
							onOpen={handleSomeDropdownOpen}
							onClose={handleSomeDropdownClose}
							label={"Facility Type"}
							selectSx={regularFieldSx}
							items={[
								{ value: DepartmentType.SNF, label: "SNF" },
								{ value: DepartmentType.ALF, label: "ALF" },
								{ value: DepartmentType.Hospital, label: "Hospital" },
							]}
							selectedItem={configSlimData.departmentType}
							onChange={(item) => {
								if (item) {
									handleSetConfigSlimData({
										departmentType: item,
									});
								}
							}}
						/>
					</Box>
					<Divider flexItem />
				</>
			)}
			{/* Roles/Jobs + Set Hours (non-synergy) */}
			{!isSynergyMS && (
				<>
					<Box
						ref={editTimePopoverParentRef}
						sx={{
							display: "flex",
							gap: 2,
							width: "100%",
							alignItems: "start",
							zIndex: 2,
						}}
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 0.5,
							}}
						>
							{jobManagementEnabled ? (
								<>
									<Typography sx={smallLabelTypographySx}>Choose Job</Typography>
									<JobSelector
										variant="dropdown"
										jobs={enabledJobs}
										selectedJobIds={configSlimData.jobId ? [configSlimData.jobId] : []}
										onSelectedJobsChanged={(selected) => handleSelectedJobChanged(selected[0] ?? "")}
										hideAllOption
										singleSelect
										onOpen={handleSomeDropdownOpen}
										onClose={handleSomeDropdownClose}
										formControlSx={{ width: "200px" }}
									/>
								</>
							) : (
								<>
									<Typography sx={smallLabelTypographySx}>Choose Roles</Typography>
									<RoleChips
										onSelectedRoleFiltersChanged={handleSelectedRoleFiltersChanged}
										initiallySelected={configSlimData.roles.map((role) => (role === "all" ? "all" : NurseRole[NurseFullRoleShort[role]]))}
									/>
								</>
							)}
						</Box>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 0.5,
							}}
						>
							<Typography sx={smallLabelTypographySx}>Set Hours</Typography>
							<TinyPopover
								isOpen={editTimeOpen}
								parentElement={editTimePopoverParentRef.current ?? undefined}
								onClickOutside={(e) => {
									e.stopPropagation();
									e.preventDefault();
									setEditTimeOpen(false);
								}}
								positions={["right"]}
								content={
									<Box
										sx={(theme) => ({
											bgcolor: SURFACE,
											boxShadow: `0px 0px 8px 4px ${alpha(theme.palette.primary.main, ALPHA.SHADOW_HEAVY)}`,
											borderRadius: "4px",
										})}
									>
										<EditTimePopover
											onTimeChange={(values) => {
												handleSetConfigSlimData({
													shiftStartTime: values.newStartTime.toFormat("HH:mm"),
													shiftEndTime: values.newEndTime.toFormat("HH:mm"),
												});
												setEditTimeOpen(false);
											}}
											onClose={(event) => {
												event.preventDefault();
												event.stopPropagation();
												setEditTimeOpen(false);
											}}
											anchorEl={null}
											isActual={false}
											menuWrapper={false}
											disableAutoCompetePortal
											startTime={times.start}
											endTime={times.end}
										/>
									</Box>
								}
							>
								<Box
									sx={{
										border: `1px solid ${NEUTRAL[300]}`,
										borderRadius: "4px",
										cursor: "pointer",
										display: "flex",
										p: 1,
										alignItems: "center",
										justifyContent: "space-between",
										width: regularFieldWidth,
										fontSize: "13px",
										fontWeight: 500,
										color: alpha(INK.SECONDARY, 0.5),
										":hover": {
											backgroundColor: alpha(INK_BLACK, 0.01),
										},
									}}
									onClick={(_) => setEditTimeOpen((prevState) => !prevState)}
								>
									{times?.start.isValid && times.end.isValid ? `${times.start.toFormat("h:mma")} - ${times.end.toFormat("h:mma")}` : "Set Hours"}
									<EditIcon fontSize={"inherit"} />
								</Box>
							</TinyPopover>
						</Box>
					</Box>
					<Divider flexItem />
				</>
			)}
			{/* Staffing targets — PPD/HPPD departments. Ratio departments are staffed by the ladder alone. */}
			{!canEditRatioLadder && (
				<>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
							width: "100%",
						}}
					>
						<Typography sx={smallLabelTypographySx}>{t("schedule.template.staffing.targets")}</Typography>
						{configSlimData.dayAmountRows.map((row, index) => {
							const disabledShortDays = disabledDaysPerRow[row.id] ?? [];
							const disabledDaysInWeek = Object.values(DaysInWeek).filter((fullDay) =>
								disabledShortDays.some((short) => fullDay.toLowerCase().startsWith(short)),
							);
							const isLastRow = index === configSlimData.dayAmountRows.length - 1;
							return (
								<Box
									key={row.id}
									sx={{
										display: "flex",
										gap: 1,
										alignItems: "center",
									}}
								>
									<DaysInWeekSelector
										size="small"
										gap="2px"
										selectedDays={Object.values(DaysInWeek).filter((fullDay) => row.days.some((short) => fullDay.toLowerCase().startsWith(short)))}
										disabledDays={disabledDaysInWeek}
										disabledTooltipText={t("schedule.template.day.already.selected")}
										onChange={(selectedDays) =>
											handleUpdateRow(row.id, {
												days: selectedDays.map((d) => d.toLowerCase().slice(0, 3)),
											})
										}
										weekStartDay={dialogWeekStartDay}
									/>
									<SystemValidationTextField
										label={t("schedule.template.target")}
										inputType={"number"}
										value={row.amount.toString()}
										onChange={(event) =>
											handleUpdateRow(row.id, {
												amount: Number.parseInt(event.target.value, 10) || 0,
											})
										}
										inputProps={{ inputProps: { min: 0 } }}
										inputSx={smallNumericFieldSx}
										reserveHeightForHelperText={false}
									/>
									<SystemValidationTextField
										label={t("schedule.template.required")}
										inputType={"number"}
										value={row.amountRequired.toString()}
										onChange={(event) =>
											handleUpdateRow(row.id, {
												amountRequired: Number.parseInt(event.target.value, 10) || 0,
											})
										}
										inputProps={{ inputProps: { min: 0, max: row.amount } }}
										inputSx={smallNumericFieldSx}
										reserveHeightForHelperText={false}
									/>
									<InHouseTooltip
										title={rowHasOccupiedDays[row.id] ? t("schedule.template.cannot.delete.target.with.shifts") : ""}
										disableHoverListener={!rowHasOccupiedDays[row.id]}
									>
										<span>
											<IconButton
												size="small"
												onClick={(e) => {
													e.stopPropagation();
													handleRemoveRow(row.id);
												}}
												disabled={configSlimData.dayAmountRows.length <= 1 || rowHasOccupiedDays[row.id]}
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</span>
									</InHouseTooltip>
									{isLastRow && (
										<IconButton
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												handleAddRow();
											}}
										>
											<AddIcon fontSize="small" />
										</IconButton>
									)}
								</Box>
							);
						})}
					</Box>
					<Divider flexItem />
				</>
			)}
			{/* Ratio Ladder — the only staffing input in a ratio department; it must cover census 0–∞ */}
			{canEditRatioLadder && (
				<>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", alignItems: "start" }}>
						<Typography sx={smallLabelTypographySx}>{t("schedule.template.ratio.ladder")}</Typography>
						<RatioLadderSection
							ratioLadder={configSlimData.ratioLadder}
							onRatioLadderChange={(ratioLadder) => handleSetConfigSlimData({ ratioLadder })}
							locationRoles={locationRoles}
							locationJobId={locationJobId}
							jobManagementEnabled={jobManagementEnabled}
						/>
						{ratioLadderGapText && (
							<Typography role="alert" data-testid="ratio-ladder-gap-message" sx={{ ...smallLabelTypographySx, color: STATUS.ERROR.main, maxWidth: "500px" }}>
								{ratioLadderGapText}
							</Typography>
						)}
					</Box>
					<Divider flexItem />
				</>
			)}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
					width: "100%",
					alignItems: "start",
				}}
			>
				<SystemSwitch
					checked={configSlimData.cantMove}
					onChange={(event) => handleSetConfigSlimData({ cantMove: event.target.checked })}
					label={t("schedule.template.cant.move")}
					dataTestId="location-cant-move-toggle"
				/>
				<Typography sx={{ ...smallLabelTypographySx, maxWidth: "500px" }}>{t("schedule.template.cant.move.helper")}</Typography>
			</Box>
			<Divider flexItem />
			{/*
			 * unmountOnExit keeps a shut Collapse from claiming the parent's flex `gap`, and the
			 * explicit width stops it collapsing to content width under `alignItems: "start"`.
			 */}
			<Collapse in={willUpdateExistingShifts} timeout={MOTION.DURATION.ENTRANCE_MD} unmountOnExit sx={revealSx}>
				<UpdateShiftsWarning changes={shiftMatchChanges} />
			</Collapse>
			{/* Set Affect Date — on edit, and on an out-of-config create that will move real shifts.
			    Collapse defaults to appear=false, so edit mode renders it open without animating. */}
			<Collapse
				in={mode === EditDepartmentLocationConfigMode.Edit || willUpdateExistingShifts}
				timeout={MOTION.DURATION.ENTRANCE_MD}
				unmountOnExit
				sx={revealSx}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 0.5,
						alignItems: "start",
					}}
				>
					<Typography sx={smallLabelTypographySx}>Set the affect date</Typography>
					{/* Disabled when the save cannot reach a shift: the server skips the cascade, so the date
					    would be accepted and then ignored. The tooltip needs an enabled wrapper element to
					    receive the hover, hence InHouseTooltip around the box rather than on it. */}
					<InHouseTooltip title={applyFromDateAffectsShifts ? "" : t("schedule.template.applyFrom.noShiftsAffected")}>
						<Box
							data-testid="location-apply-from-date"
							aria-disabled={!applyFromDateAffectsShifts}
							sx={{
								padding: "2px",
								border: `1px solid ${NEUTRAL[400]}`,
								borderRadius: "4px",
								fontSize: "15px",
								color: applyFromDateAffectsShifts ? INK.SECONDARY : alpha(INK.SECONDARY, 0.4),
								cursor: applyFromDateAffectsShifts ? "pointer" : "default",
								...(applyFromDateAffectsShifts && {
									":hover": {
										backgroundColor: alpha(INK_BLACK, 0.01),
									},
								}),
							}}
							onClick={(event) => {
								event.stopPropagation();
								if (!applyFromDateAffectsShifts) {
									return;
								}
								if (!datePickerAnchorEl) {
									setDatePickerAnchorEl(event.currentTarget);
								} else {
									setDatePickerAnchorEl(null);
								}
							}}
						>
							Update from {updateFromDate?.toFormat("D")}
							{datePickerAnchorEl && (
								<SystemDatePickerPopup
									anchorEl={datePickerAnchorEl}
									open={!!datePickerAnchorEl}
									onClose={() => setDatePickerAnchorEl(null)}
									onChange={handleUpdateFromDate}
									value={updateFromDate}
									overridePickerProps={{
										minDate: DateTime.now().startOf("day"),
									}}
									disablePortal
								/>
							)}
						</Box>
					</InHouseTooltip>
				</Box>
				<Divider flexItem />
			</Collapse>
			{/* Delete location */}
			{mode === EditDepartmentLocationConfigMode.Edit &&
				(!confirmDelete ? (
					<SystemButton
						sx={{
							fontWeight: 500,
						}}
						leadingIcon={<DeleteIcon fontSize={"small"} />}
						buttonVariant={"delete"}
						disabled={!allowDelete || isMasterScheduleEditBlocked}
						tooltip={
							isMasterScheduleEditBlocked
								? t("schedule.template.edit.blocked")
								: !allowDelete
									? "You cannot delete this location because it has shifts assigned to it."
									: undefined
						}
						onClick={(e) => {
							e.stopPropagation();
							setConfirmDelete(true);
						}}
					>
						Delete Entire Row
					</SystemButton>
				) : (
					<Box
						sx={{
							display: "flex",
							gap: 1,
						}}
					>
						<SystemButton
							sx={{
								fontWeight: 500,
							}}
							leadingIcon={<DeleteIcon fontSize={"small"} />}
							buttonVariant={"delete"}
							onClick={async (e) => {
								e.stopPropagation();
								await handleDeleteLocation();
							}}
							loading={deleteLoading}
						>
							Confirm?
						</SystemButton>
						<SystemButton
							buttonVariant={"secondary"}
							onClick={(e) => {
								e.stopPropagation();
								setConfirmDelete(false);
							}}
							disabled={deleteLoading}
						>
							Cancel
						</SystemButton>
					</Box>
				))}
			{/* Save / Cancel */}
			<Box
				sx={{
					display: "flex",
					gap: 1,
					width: "100%",
					justifyContent: "end",
					// Pinned to the foot of whatever scrolls this form. In a popover short enough to be
					// clamped (see `fitViewport`), the actions would otherwise sit below the fold with
					// nothing to suggest a scroll would reach them. `sticky` is inert when the container
					// doesn't scroll, so the unclamped surfaces are unaffected.
					position: "sticky",
					// Negative, by exactly the container's bottom padding: a sticky element parks against
					// the scrollport's CONTENT edge, leaving that padding strip as scrollable area where
					// the text passing underneath would still show. A margin cannot move a stuck element,
					// so the offset itself has to reach into the strip — and `pb` keeps the buttons off it.
					bottom: `-${SCROLL_CONTAINER_PADDING_PX}px`,
					zIndex: 1,
					backgroundColor: SURFACE,
					pt: 1,
					pb: 1,
				}}
			>
				<SystemButton
					sx={{
						width: ctaButtonWidth,
					}}
					buttonVariant={"secondary"}
					onClick={(_) => onCancel?.()}
					disabled={updateDepartmentLocationsLoading || deleteLoading}
				>
					Cancel
				</SystemButton>

				<SystemButton
					data-testid="edit-location-config-submit-button"
					sx={{
						width: ctaButtonWidth,
					}}
					onClick={mode === EditDepartmentLocationConfigMode.Create ? handleCreateWithDayAmounts : handleSaveWithDayAmounts}
					disabled={!isSavedEnabled || deleteLoading || confirmDelete || isMasterScheduleEditBlocked}
					loading={updateDepartmentLocationsLoading}
					tooltip={isMasterScheduleEditBlocked ? t("schedule.template.edit.blocked") : !isSavedEnabled ? saveDisabledReason : undefined}
				>
					{mode === EditDepartmentLocationConfigMode.Create ? t("schedule.template.v3.editor.create") : t("schedule.template.v3.editor.save")}
				</SystemButton>
			</Box>
		</Box>
	);
};
